/**
 * El recorrido de las secciones esenciales, guardado por presupuesto (MADERINCO-ARBOLES-SECCIONES-T1; contrato de P
 * #30474 con SI de Ares #30514, A lo construye #30515): GET/PUT /api/v1/presupuestos/{id}/recorrido con If-Match.
 *
 * Solo viaja lo RESPONDIDO: Cubierta (tipo, q2, q3, revisar), los No aplica con su motivo y las respuestas de los
 * arboles. El servidor no calcula estados ni agrega partidas; el panel los recalcula al leer.
 * Un "no aplica" sin motivo no sale (el servidor lo rechazaria con 400): queda solo en pantalla y se dice.
 *
 * @module application/presupuestos/recorrido
 */

const ESTADOS = new Set(['confirmado', 'pendiente', 'no_aplica']);
const motivoValido = (m) => { const t = String(m || '').trim(); return t.length >= 2 && t.length <= 120 ? t : null; };

/**
 * @param {{cubierta:object, noAplica:Map<string,string>, respArbol:Map<string,{tipos:Set,checks:object,complementos:any}>, schemaVersion:string, arbolesSha:string}} o
 * @returns {{cuerpo:object, sinMotivo:number}}
 */
export function cuerpoRecorrido({ cubierta = {}, noAplica = new Map(), respArbol = new Map(), schemaVersion, arbolesSha }) {
  let sinMotivo = 0;
  const na = [];
  for (const [esencialId, m] of noAplica) { const t = motivoValido(m); if (t) na.push({ esencialId, motivo: t }); else sinMotivo++; }
  const arboles = [];
  for (const [esencialId, r] of respArbol) {
    const checks = [];
    for (const [id, v] of Object.entries(r.checks || {})) {
      if (!v || !ESTADOS.has(v.estado)) continue;
      if (v.estado === 'no_aplica') { const t = motivoValido(v.motivo); if (!t) { sinMotivo++; continue; } checks.push({ id, estado: v.estado, motivo: t }); }
      else checks.push({ id, estado: v.estado, motivo: null });
    }
    const tipos = [...(r.tipos || [])];
    if (!tipos.length && !checks.length && r.complementos == null) continue;
    arboles.push({ esencialId, tipos, checks, complementos: r.complementos ?? null });
  }
  return {
    sinMotivo,
    cuerpo: {
      schemaVersion: String(schemaVersion || ''),
      arbolesSha: String(arbolesSha || ''),
      cubierta: { tipo: cubierta.tipo ?? null, q2: cubierta.q2 ?? null, q3: cubierta.q3 ?? null, revisar: !!cubierta.revisar },
      noAplica: na,
      arboles
    }
  };
}

/**
 * Lo leido del servidor vuelve a la pantalla (tolera camelCase y PascalCase). Devuelve si el arbol cambio de version.
 * @returns {{cubierta:object, noAplica:Map<string,string>, respArbol:Map<string,object>, arbolCambio:boolean}|null}
 */
export function recorridoLeido(data, { arbolesSha } = {}) {
  const rec = data?.recorrido ?? data?.Recorrido ?? (data && (data.arboles || data.Arboles) ? data : null);
  if (!rec) return null;
  const de = (o, k) => o?.[k] ?? o?.[k[0].toUpperCase() + k.slice(1)];
  const cub = de(rec, 'cubierta') || {};
  const noAplica = new Map();
  for (const x of de(rec, 'noAplica') || []) { const t = motivoValido(de(x, 'motivo')); if (t) noAplica.set(de(x, 'esencialId'), t); }
  const respArbol = new Map();
  for (const a of de(rec, 'arboles') || []) {
    const checks = {};
    for (const c of de(a, 'checks') || []) if (ESTADOS.has(de(c, 'estado'))) checks[de(c, 'id')] = { estado: de(c, 'estado'), motivo: de(c, 'motivo') || '' };
    respArbol.set(de(a, 'esencialId'), { tipos: new Set(de(a, 'tipos') || []), checks, complementos: de(a, 'complementos') ?? null, error: '' });
  }
  const guardadoSha = de(rec, 'arbolesSha') ?? de(data, 'arbolesSha');
  return {
    cubierta: { tipo: de(cub, 'tipo') ?? null, q2: de(cub, 'q2') ?? null, q3: de(cub, 'q3') ?? null, revisar: !!de(cub, 'revisar') },
    noAplica, respArbol,
    arbolCambio: !!(guardadoSha && arbolesSha && guardadoSha !== arbolesSha)
  };
}

/** La frase de una linea que dice donde quedo el recorrido. */
export function fraseDelRecorrido(r) {
  if (r?.ok) return r.sinMotivo ? `Respuestas guardadas; ${r.sinMotivo} "no aplica" sin motivo quedan solo en pantalla.` : 'Respuestas de las secciones guardadas con el presupuesto.';
  switch (r?.motivo) {
    case 'NO_PUBLICADO': return 'Las respuestas de las secciones todavía no se guardan en el servidor: quedan en esta pantalla.';
    case 'NO_HABILITADO': return 'En este negocio las respuestas de las secciones quedan solo en esta pantalla.';
    case 'NO_ES_BORRADOR': return 'Este presupuesto ya no es borrador: sus respuestas no se cambian.';
    case 'OTRO_CAMBIO': return 'Alguien cambió este presupuesto: vuelve a guardar para no pisar sus cambios.';
    case 'INVALIDO': return 'El servidor no aceptó las respuestas: ' + (r.detalle || 'revisa los motivos de "no aplica".');
    case 'SIN_RED': return 'Sin conexión: las respuestas quedan en esta pantalla.';
    default: return 'No pudimos guardar las respuestas de las secciones; quedan en esta pantalla.';
  }
}
